import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, ChevronRight, ChevronLeft, CalendarDays, Phone, AlertTriangle, CheckCircle2, ArrowRight, Info, Users, Clock, FileText, Car, Banknote, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

const PFLEGEGRADE = [
  { value: "2", label: "Pflegegrad 2", betrag: "474 bis 3.539" },
  { value: "3", label: "Pflegegrad 3", betrag: "817,50 bis 3.539" },
  { value: "4", label: "Pflegegrad 4", betrag: "1.092 bis 3.539" },
  { value: "5", label: "Pflegegrad 5", betrag: "1.351,50 bis 3.539" },
];

const ERSATZPERSONEN = [
  { value: "tochter", label: "Tochter", verwandt: true },
  { value: "sohn", label: "Sohn", verwandt: true },
  { value: "ehepartner", label: "Ehepartner/in", verwandt: true },
  { value: "enkel", label: "Enkel/in", verwandt: true },
  { value: "nachbar", label: "Nachbar/in", verwandt: false },
  { value: "freund", label: "Freund/in", verwandt: false },
  { value: "bekannter", label: "Bekannte/r", verwandt: false },
  { value: "sonstige", label: "Sonstige Privatperson", verwandt: false },
  { value: "profi", label: "Professioneller Anbieter", verwandt: false },
];

const TOTAL_STEPS = 7;

const FAQ_ITEMS = [
  { q: "Wie f\u00fclle ich den Antrag auf Verhinderungspflege aus?", a: "Den Antrag stellen Sie bei Ihrer Pflegekasse. Sie m\u00fcssen angeben, wer die Pflege \u00fcbernommen hat, in welchem Zeitraum und wie viele Stunden. Unser Assistent hilft Ihnen, alle relevanten Angaben zusammenzustellen. F\u00fcr eine individuelle Pr\u00fcfung empfehlen wir unsere kostenfreie Pflegeberatung." },
  { q: "Wer darf Verhinderungspflege \u00fcbernehmen?", a: "Grunds\u00e4tzlich kann jede Person die Verhinderungspflege \u00fcbernehmen: Angeh\u00f6rige, Nachbarn, Freunde oder professionelle Pflegedienste. Die H\u00f6he der Erstattung h\u00e4ngt davon ab, ob die Ersatzpflegeperson mit dem Pflegebed\u00fcrftigen verwandt ist oder nicht." },
  { q: "Welche Nachweise brauche ich?", a: "In der Regel ben\u00f6tigen Sie: eine Best\u00e4tigung \u00fcber den Zeitraum der Verhinderungspflege, Angaben zur Ersatzpflegeperson, bei professionellen Anbietern eine Rechnung und bei Angehörigen ggf. Nachweise \u00fcber Fahrkosten oder Verdienstausfall." },
  { q: "K\u00f6nnen Angeh\u00f6rige Verhinderungspflege abrechnen?", a: "Ja. Wenn Angeh\u00f6rige (bis zum 2. Grad verwandt oder verschw\u00e4gert) die Verhinderungspflege \u00fcbernehmen, wird in der Regel maximal das 1,5-fache des Pflegegeldes erstattet. Zus\u00e4tzliche Kosten wie Fahrkosten oder nachgewiesener Verdienstausfall k\u00f6nnen separat erstattet werden." },
  { q: "Was ist bei stundenweiser Verhinderungspflege zu beachten?", a: "Bei stundenweiser Verhinderungspflege (unter 8 Stunden pro Tag) wird das Pflegegeld nicht gek\u00fcrzt. Es werden nur die tats\u00e4chlich angefallenen Kosten vom Jahresbudget abgezogen. Die 42 Kalendertage pro Jahr werden nicht belastet." },
  { q: "Was ist bei tageweiser Verhinderungspflege zu beachten?", a: "Bei tageweiser Verhinderungspflege (8 Stunden oder mehr pro Tag) wird das Pflegegeld f\u00fcr den Zeitraum um 50% gek\u00fcrzt. Die Verhinderungspflege ist auf maximal 42 Kalendertage pro Jahr begrenzt. Das gemeinsame Jahresbudget (VP + KZP) betr\u00e4gt bis zu 3.539 Euro." },
];

function computeResult(form) {
  const pg = parseInt(form.pflegegrad, 10);
  const person = ERSATZPERSONEN.find(p => p.value === form.ersatzperson);
  const isVerwandt = form.verwandt === "ja";
  const isProfi = form.ersatzperson === "profi";
  const isStundenweise = form.art === "stundenweise";
  const tage = parseInt(form.tage, 10) || 0;
  const stunden = parseInt(form.stunden, 10) || 0;
  const hatFahrkosten = form.kosten.includes("fahrkosten");
  const hatVerdienstausfall = form.kosten.includes("verdienstausfall");
  const hatSonstiges = form.kosten.includes("sonstiges");

  // Pflegegeld-basierte Beträge für Verwandte bis 2. Grad
  const pflegegeldMap = { 2: 347, 3: 599, 4: 800, 5: 990 };
  const maxVerwandt = Math.round((pflegegeldMap[pg] || 316) * 1.5 * 100) / 100;

  let maxBetrag = 1612;
  let erstattungHinweis = "";
  if (isProfi) {
    erstattungHinweis = "Bei professionellen Anbietern werden die tats\u00e4chlichen Kosten bis maximal 3.539 \u20ac pro Jahr erstattet.";
  } else if (isVerwandt) {
    erstattungHinweis = `Bei verwandten Ersatzpflegepersonen (bis 2. Grad) wird in der Regel maximal das 1,5-fache des Pflegegeldes erstattet: bis zu ${maxVerwandt.toLocaleString("de-DE")} \u20ac. Zus\u00e4tzliche Kosten (Fahrt, Verdienstausfall) k\u00f6nnen bis zur Gesamtgrenze von 3.539 \u20ac geltend gemacht werden.`;
    maxBetrag = maxVerwandt;
  } else {
    erstattungHinweis = "Bei nicht verwandten Privatpersonen werden die tats\u00e4chlichen Kosten bis maximal 3.539 \u20ac pro Jahr erstattet.";
  }

  let pflegegeldHinweis = "";
  if (isStundenweise) {
    pflegegeldHinweis = "Bei stundenweiser Verhinderungspflege (unter 8 Std./Tag) wird Ihr Pflegegeld nicht gek\u00fcrzt.";
  } else {
    pflegegeldHinweis = "Bei tageweiser Verhinderungspflege wird das Pflegegeld f\u00fcr den Zeitraum um 50 % gek\u00fcrzt.";
  }

  // Typische Fehler
  const fehler = [];
  if (isVerwandt) {
    fehler.push("H\u00e4ufiger Fehler: Verwandte tragen den vollen Betrag von 3.539 \u20ac ein \u2014 bei Verwandten bis zum 2. Grad ist die Erstattung aber auf das 1,5-fache des Pflegegeldes begrenzt.");
    fehler.push("Tipp: Zus\u00e4tzliche Kosten wie Fahrkosten oder Verdienstausfall separat angeben \u2014 diese werden \u00fcber die Pflegegeld-Grenze hinaus bis 3.539 \u20ac erstattet.");
  }
  if (!isVerwandt && !isProfi) {
    fehler.push("H\u00e4ufiger Fehler bei Nachbarn/Bekannten: Keine Quittung oder Vereinbarung erstellt. Halten Sie Art und Umfang der Pflege schriftlich fest.");
  }
  if (isStundenweise) {
    fehler.push("Wichtig bei stundenweiser Verhinderungspflege: Dokumentieren Sie die genauen Uhrzeiten, um nachzuweisen, dass die Vertretung unter 8 Stunden pro Tag betrug.");
  } else {
    fehler.push("Beachten Sie: Bei tageweiser Verhinderungspflege (ab 8 Std./Tag) wird jeder angefangene Tag auf das Maximum von 42 Kalendertagen angerechnet.");
    if (tage > 42) {
      fehler.push("Achtung: Sie haben mehr als 42 Tage angegeben. Die Verhinderungspflege ist auf maximal 42 Kalendertage pro Jahr begrenzt.");
    }
  }
  if (isProfi) {
    fehler.push("Bei professionellen Anbietern: Achten Sie darauf, dass der Anbieter eine detaillierte Rechnung ausstellt, die den Zeitraum und die erbrachten Leistungen aufschl\u00fcsselt.");
  }

  // Checkliste
  const checkliste = [
    { text: `Pflegegrad ${pg} ist anerkannt`, ok: true },
    { text: "Hauptpflegeperson war mindestens 6 Monate vor der Verhinderungspflege t\u00e4tig", ok: true },
    { text: `Ersatzpflegeperson: ${person ? person.label : form.ersatzperson}`, ok: true },
    { text: `Zeitraum: ${tage} Tag${tage !== 1 ? "e" : ""}, ${stunden} Stunden/Tag`, ok: tage > 0 && stunden > 0 },
    { text: `Art: ${isStundenweise ? "Stundenweise" : "Tageweise"} Verhinderungspflege`, ok: true },
  ];
  if (isVerwandt) checkliste.push({ text: "Verwandtschaftsverh\u00e4ltnis dokumentiert", ok: true });
  if (hatFahrkosten) checkliste.push({ text: "Fahrkostennachweise vorbereitet", ok: true });
  if (hatVerdienstausfall) checkliste.push({ text: "Verdienstausfall-Nachweis vorbereitet", ok: true });
  if (isProfi) checkliste.push({ text: "Rechnung des Pflegedienstes angefordert", ok: true });
  checkliste.push({ text: "Antrag vollst\u00e4ndig gepr\u00fcft und unterschrieben", ok: false });

  const nachweise = [];
  nachweise.push("Ausgef\u00fcllter Antrag auf Verhinderungspflege (Formular Ihrer Pflegekasse)");
  if (isProfi) nachweise.push("Rechnung des professionellen Pflegedienstes");
  if (!isProfi) nachweise.push("Schriftliche Best\u00e4tigung der Ersatzpflegeperson \u00fcber den Zeitraum");
  if (hatFahrkosten) nachweise.push("Fahrkostennachweise (Tankquittungen, Bahntickets o. \u00c4.)");
  if (hatVerdienstausfall) nachweise.push("Arbeitgeberbescheinigung \u00fcber Verdienstausfall");
  if (isVerwandt) nachweise.push("Angabe des Verwandtschaftsgrades zur pflegebed\u00fcrftigen Person");

  return { erstattungHinweis, pflegegeldHinweis, maxBetrag, fehler, checkliste, nachweise, person, isVerwandt, isProfi, isStundenweise, pg, tage, stunden, hatFahrkosten, hatVerdienstausfall, hatSonstiges };
}

export default function VerhinderungspflegeAssistent() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    pflegegrad: "", ersatzperson: "", verwandt: "", tage: "", stunden: "", art: "", kosten: [],
  });
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Verhinderungspflege-Antragsassistent | Ausfüllhilfe Schritt für Schritt";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Verhinderungspflege Antrag richtig ausfüllen: Schritt-für-Schritt-Assistent mit Ausfüllhilfe, Checkliste und typischen Fehlern. Kostenlose Orientierung für Angehörige.");
  }, []);

  const toggleKosten = (val) => {
    setForm(prev => ({
      ...prev,
      kosten: prev.kosten.includes(val) ? prev.kosten.filter(v => v !== val) : [...prev.kosten, val]
    }));
  };

  const canNext = () => {
    if (step === 1) return form.pflegegrad !== "";
    if (step === 2) return form.ersatzperson !== "";
    if (step === 3) return form.verwandt !== "";
    if (step === 4) return form.tage !== "" && parseInt(form.tage, 10) > 0;
    if (step === 5) return form.stunden !== "" && parseInt(form.stunden, 10) > 0;
    if (step === 6) return form.art !== "";
    if (step === 7) return true;
    return false;
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const r = computeResult(form);
      setResult(r);
      setShowResult(true);
      setTimeout(() => document.getElementById("ergebnis")?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  const handleBack = () => {
    if (step > 1) { setStep(step - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };

  const handleReset = () => {
    setShowResult(false);
    setResult(null);
    setStep(1);
    setForm({ pflegegrad: "", ersatzperson: "", verwandt: "", tage: "", stunden: "", art: "", kosten: [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const progress = showResult ? 100 : Math.round(((step - 1) / TOTAL_STEPS) * 100);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="vp-assistent">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 shrink-0 overflow-hidden">
              <img src={ICON_URL} alt="Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" />
            </div>
            <div>
              <span className="text-sm font-bold text-slate-800 font-['Manrope'] block leading-tight">Lehnen & Weinacht</span>
              <span className="text-[9px] text-cyan-600 font-semibold tracking-wider uppercase">Pflegeberatung & Alltagsbegleitung</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm text-slate-500 hover:text-cyan-700 transition-colors hidden sm:inline">Ratgeber</Link>
            <a href="/#termin-buchen" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-all active:scale-95">Termin buchen</a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8">
          <Link to="/" className="hover:text-cyan-700 transition-colors">Startseite</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-cyan-700 transition-colors">Ratgeber</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-600">Verhinderungspflege-Assistent</span>
        </nav>

        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 px-4 py-1.5 rounded-full mb-4">
            <ClipboardCheck className="h-4 w-4" />
            Schritt-für-Schritt Ausfüllhilfe
          </div>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 leading-tight mb-3">
            Verhinderungspflege-Antragsassistent
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            In wenigen Minuten zur Ausfüllhilfe für Ihren Verhinderungspflege-Antrag.
          </p>
        </div>

        {/* Progress bar */}
        {!showResult && (
          <div className="mb-8" data-testid="vp-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">Schritt {step} von {TOTAL_STEPS}</span>
              <span className="text-sm font-medium text-cyan-700">{progress}%</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-600 to-green-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Steps */}
        {!showResult && (
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-lg shadow-cyan-900/5 p-6 sm:p-8 mb-10" data-testid="vp-form">

            {/* Step 1: Pflegegrad */}
            {step === 1 && (
              <div data-testid="vp-step-1">
                <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Welchen Pflegegrad hat die pflegebedürftige Person?</h2>
                <p className="text-sm text-slate-500 mb-6">Verhinderungspflege steht ab Pflegegrad 2 zu.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PFLEGEGRADE.map(pg => (
                    <button key={pg.value} type="button" data-testid={`vp-pg-${pg.value}`}
                      onClick={() => setForm({ ...form, pflegegrad: pg.value })}
                      className={`p-5 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${form.pflegegrad === pg.value ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200" : "border-slate-200 hover:border-cyan-200"}`}>
                      <span className="text-2xl font-extrabold text-slate-800 block font-['Manrope']">{pg.value}</span>
                      <span className="text-xs text-slate-500">Pflegegrad</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Ersatzperson */}
            {step === 2 && (
              <div data-testid="vp-step-2">
                <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Wer hat die Pflege übernommen?</h2>
                <p className="text-sm text-slate-500 mb-6">Wählen Sie die Ersatzpflegeperson aus.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {ERSATZPERSONEN.map(ep => (
                    <button key={ep.value} type="button" data-testid={`vp-person-${ep.value}`}
                      onClick={() => setForm({ ...form, ersatzperson: ep.value, verwandt: ep.verwandt ? "ja" : (ep.value === "profi" ? "nein" : form.verwandt) })}
                      className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${form.ersatzperson === ep.value ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200" : "border-slate-200 hover:border-cyan-200"}`}>
                      <Users className={`h-5 w-5 mx-auto mb-1.5 ${form.ersatzperson === ep.value ? "text-cyan-700" : "text-slate-400"}`} />
                      <span className="text-sm font-bold text-slate-800">{ep.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Verwandt */}
            {step === 3 && (
              <div data-testid="vp-step-3">
                <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Ist die Ersatzpflegeperson mit der pflegebedürftigen Person verwandt oder verschwägert (bis zum 2. Grad)?</h2>
                <p className="text-sm text-slate-500 mb-6">Dies beeinflusst die Höhe der Erstattung.</p>
                <div className="grid grid-cols-2 gap-3 max-w-sm">
                  {[{ value: "ja", label: "Ja" }, { value: "nein", label: "Nein" }].map(opt => (
                    <button key={opt.value} type="button" data-testid={`vp-verwandt-${opt.value}`}
                      onClick={() => setForm({ ...form, verwandt: opt.value })}
                      className={`p-5 rounded-xl border-2 text-center transition-all active:scale-[0.97] text-xl font-bold ${form.verwandt === opt.value ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200 text-cyan-700" : "border-slate-200 hover:border-cyan-200 text-slate-700"}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="mt-4 p-4 bg-cyan-50/50 rounded-xl border border-cyan-100">
                  <p className="text-xs text-cyan-800"><strong>Verwandt bis 2. Grad:</strong> Eltern, Kinder, Großeltern, Enkel, Geschwister sowie Schwiegereltern, Schwiegerkinder, Stiefeltern, Stiefkinder.</p>
                </div>
              </div>
            )}

            {/* Step 4: Tage */}
            {step === 4 && (
              <div data-testid="vp-step-4">
                <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Wie viele Tage wurde die Pflege übernommen?</h2>
                <p className="text-sm text-slate-500 mb-6">Maximal 42 Kalendertage pro Jahr bei tageweiser Verhinderungspflege.</p>
                <input type="number" min="1" max="365" data-testid="vp-tage" value={form.tage}
                  onChange={e => setForm({ ...form, tage: e.target.value })} placeholder="z. B. 14"
                  className="w-full max-w-xs h-14 rounded-xl border border-slate-200 bg-white px-4 text-lg focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400" />
                {parseInt(form.tage, 10) > 42 && (
                  <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800">Hinweis: Bei tageweiser Verhinderungspflege sind maximal 42 Tage pro Jahr möglich.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Stunden */}
            {step === 5 && (
              <div data-testid="vp-step-5">
                <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Wie viele Stunden pro Tag wurde die Pflege übernommen?</h2>
                <p className="text-sm text-slate-500 mb-6">Unter 8 Stunden gilt als stundenweise Verhinderungspflege — das Pflegegeld wird dann nicht gekürzt.</p>
                <input type="number" min="1" max="24" data-testid="vp-stunden" value={form.stunden}
                  onChange={e => setForm({ ...form, stunden: e.target.value })} placeholder="z. B. 6"
                  className="w-full max-w-xs h-14 rounded-xl border border-slate-200 bg-white px-4 text-lg focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400" />
                {parseInt(form.stunden, 10) >= 8 && (
                  <div className="mt-3 p-3 bg-cyan-50 rounded-xl border border-cyan-100">
                    <p className="text-xs text-cyan-800"><strong>Hinweis:</strong> Ab 8 Stunden pro Tag gilt die Verhinderungspflege als tageweise. Das Pflegegeld wird für diesen Zeitraum um 50 % gekürzt.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 6: Stundenweise / Tageweise */}
            {step === 6 && (
              <div data-testid="vp-step-6">
                <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Wurde die Pflege stundenweise oder tageweise durchgeführt?</h2>
                <p className="text-sm text-slate-500 mb-6">Stundenweise = unter 8 Stunden pro Tag. Tageweise = 8 Stunden oder mehr.</p>
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {[
                    { value: "stundenweise", label: "Stundenweise", desc: "Unter 8 Std./Tag", icon: Clock },
                    { value: "tageweise", label: "Tageweise", desc: "8 Std. oder mehr/Tag", icon: CalendarDays },
                  ].map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button key={opt.value} type="button" data-testid={`vp-art-${opt.value}`}
                        onClick={() => setForm({ ...form, art: opt.value })}
                        className={`p-5 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${form.art === opt.value ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200" : "border-slate-200 hover:border-cyan-200"}`}>
                        <Icon className={`h-6 w-6 mx-auto mb-2 ${form.art === opt.value ? "text-cyan-700" : "text-slate-400"}`} />
                        <span className="text-base font-bold text-slate-800 block">{opt.label}</span>
                        <span className="text-xs text-slate-500">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 7: Zusätzliche Kosten */}
            {step === 7 && (
              <div data-testid="vp-step-7">
                <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Sind zusätzliche Kosten angefallen?</h2>
                <p className="text-sm text-slate-500 mb-6">Mehrfachauswahl möglich. Wenn keine Zusatzkosten angefallen sind, klicken Sie direkt auf „Ausfüllhilfe erstellen".</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { value: "fahrkosten", label: "Fahrkosten", desc: "An-/Abreise der Ersatzperson", icon: Car },
                    { value: "verdienstausfall", label: "Verdienstausfall", desc: "Einkommensverlust der Ersatzperson", icon: Banknote },
                    { value: "sonstiges", label: "Sonstige Kosten", desc: "Weitere Aufwendungen", icon: HelpCircle },
                  ].map(opt => {
                    const Icon = opt.icon;
                    const sel = form.kosten.includes(opt.value);
                    return (
                      <button key={opt.value} type="button" data-testid={`vp-kosten-${opt.value}`}
                        onClick={() => toggleKosten(opt.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all active:scale-[0.97] ${sel ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200" : "border-slate-200 hover:border-cyan-200"}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-5 w-5 ${sel ? "text-cyan-700" : "text-slate-400"}`} />
                          <span className="text-sm font-bold text-slate-800">{opt.label}</span>
                        </div>
                        <span className="text-xs text-slate-500">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
              {step > 1 ? (
                <button onClick={handleBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-cyan-700 transition-colors" data-testid="vp-back-btn">
                  <ChevronLeft className="h-4 w-4" /> Zurück
                </button>
              ) : <div />}
              <Button onClick={handleNext} disabled={!canNext()} data-testid="vp-next-btn"
                className="bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-300 text-white rounded-xl h-12 px-8 text-base font-semibold shadow-md hover:shadow-lg transition-all">
                {step === TOTAL_STEPS ? (
                  <><ClipboardCheck className="h-4 w-4 mr-2" /> Ausfüllhilfe erstellen</>
                ) : (
                  <>Weiter <ChevronRight className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {showResult && result && (
          <div id="ergebnis" data-testid="vp-ergebnis">
            {/* Ausfüllhilfe */}
            <div className="bg-white rounded-2xl border border-green-200 shadow-lg shadow-green-900/5 p-6 sm:p-8 mb-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-10 w-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-green-700" />
                </div>
                <div>
                  <h2 className="font-['Manrope'] text-xl font-bold text-slate-900">Ihre individuelle Ausfüllhilfe</h2>
                  <p className="text-xs text-slate-500">Für Ihren Verhinderungspflege-Antrag</p>
                </div>
              </div>

              <p className="text-base text-slate-600 mb-5 leading-relaxed">
                Für Ihren Fall sollten insbesondere folgende Angaben im Antrag geprüft werden:
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Ersatzpflegeperson</p>
                  <p className="text-base font-bold text-slate-800">{result.person?.label || "–"} {result.isVerwandt ? "(verwandt)" : "(nicht verwandt)"}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Zeitraum</p>
                  <p className="text-base font-bold text-slate-800">{result.tage} Tag{result.tage !== 1 ? "e" : ""}, {result.stunden} Stunden pro Tag</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Art der Verhinderungspflege</p>
                  <p className="text-base font-bold text-slate-800">{result.isStundenweise ? "Stundenweise" : "Tageweise"} Verhinderungspflege</p>
                </div>
                <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-100">
                  <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wider mb-1">Erstattung</p>
                  <p className="text-sm text-cyan-800 leading-relaxed">{result.erstattungHinweis}</p>
                </div>
                <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-100">
                  <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wider mb-1">Pflegegeld</p>
                  <p className="text-sm text-cyan-800 leading-relaxed">{result.pflegegeldHinweis}</p>
                </div>
              </div>

              {/* Nachweise */}
              <h3 className="text-base font-bold text-slate-800 mb-3">Benötigte Nachweise</h3>
              <ul className="space-y-2 mb-6">
                {result.nachweise.map((n, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <FileText className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span className="text-sm text-slate-600">{n}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Typische Fehler */}
            <div className="bg-white rounded-2xl border border-amber-200 shadow-lg shadow-amber-900/5 p-6 sm:p-8 mb-6" data-testid="vp-fehler">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-10 w-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-700" />
                </div>
                <h3 className="font-['Manrope'] text-lg font-bold text-slate-900">Typische Fehler vermeiden</h3>
              </div>
              <ul className="space-y-3">
                {result.fehler.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                    <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <span className="text-sm text-amber-800 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Checkliste */}
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-lg shadow-cyan-900/5 p-6 sm:p-8 mb-6" data-testid="vp-checkliste">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-10 w-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="h-5 w-5 text-green-700" />
                </div>
                <h3 className="font-['Manrope'] text-lg font-bold text-slate-900">Ihre Checkliste</h3>
              </div>
              <ul className="space-y-2">
                {result.checkliste.map((item, i) => (
                  <li key={i} className={`flex items-center gap-3 p-3 rounded-xl ${item.ok ? "bg-green-50/50 border border-green-100" : "bg-slate-50 border border-slate-100"}`}>
                    <CheckCircle2 className={`h-5 w-5 shrink-0 ${item.ok ? "text-green-600" : "text-slate-300"}`} />
                    <span className={`text-sm ${item.ok ? "text-slate-700" : "text-slate-500"}`}>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-cyan-50 to-green-50 rounded-2xl border border-cyan-100 p-8 mb-8" data-testid="vp-cta">
              <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-3">Unsicher bei Ihrem Antrag?</h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                Unsere Pflegeberatung unterstützt Sie bei Fragen rund um Verhinderungspflege, Entlastungsleistungen, Pflegegrad und Beratungsbesuche nach § 37 Abs. 3 SGB XI — kostenfrei und ohne bürokratischen Aufwand.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/#termin-buchen" data-testid="vp-cta-termin" className="inline-flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]">
                  <CalendarDays className="h-5 w-5" /> Kostenlose Beratung anfragen
                </a>
                <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]">
                  <Phone className="h-5 w-5" /> 0163 791 7044
                </a>
              </div>
            </div>

            {/* Reset */}
            <div className="text-center mb-10">
              <Button onClick={handleReset} variant="outline" className="rounded-xl px-8 h-12 text-base" data-testid="vp-reset-btn">
                Neue Ausfüllhilfe erstellen
              </Button>
            </div>
          </div>
        )}

        {/* FAQ */}
        <section className="mb-12" data-testid="vp-faq">
          <h2 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-6">Häufige Fragen zur Verhinderungspflege</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200/60 rounded-xl px-5 shadow-sm">
                <AccordionTrigger className="text-base font-semibold text-slate-800 hover:text-cyan-700 hover:no-underline py-4">{item.q}</AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed pb-4">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related articles */}
        <section className="mb-12">
          <h3 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Weiterführende Artikel</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { slug: "sozialraumbudget-verhinderungspflege", title: "Sozialraumbudget und Verhinderungspflege", category: "Verhinderungspflege" },
              { slug: "sozialraumbudget-pflege", title: "Sozialraumbudget in der Pflege", category: "Sozialraumbudget" },
              { slug: "sozialraumbudget-entlastungsbetrag", title: "Entlastungsbetrag: Was ist der Unterschied?", category: "Entlastungsbetrag" },
              { slug: "pflegebegleitung-angehoerige", title: "Pflegebegleitung für Angehörige", category: "Pflegebegleitung" },
            ].map(a => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="bg-white border border-slate-200/60 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group">
                <span className="text-xs font-medium text-cyan-700 bg-cyan-50 px-2 py-1 rounded-full">{a.category}</span>
                <p className="text-sm font-semibold text-slate-800 mt-2 group-hover:text-cyan-700 transition-colors">{a.title}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <div className="bg-slate-100 rounded-xl p-5 mb-10 text-sm text-slate-500 italic">
          Dieser Assistent dient der allgemeinen Orientierung und ersetzt keine individuelle Pflegeberatung oder Rechtsberatung. Die Angaben basieren auf den aktuellen gesetzlichen Regelungen (Stand 2026). Für eine verbindliche Prüfung Ihres Antrags nutzen Sie bitte unsere kostenfreie Pflegeberatung.
        </div>

        <div className="text-center pt-6 border-t border-slate-200 mb-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-cyan-700 hover:underline font-medium">
            <ArrowRight className="h-4 w-4 rotate-180" /> Alle Ratgeber-Artikel
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex justify-center gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
            <a href="/#kontakt" className="hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>

      {/* FAQPage Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": FAQ_ITEMS.map(item => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } }))
      }) }} />
    </div>
  );
}
